import { all, put, select, call } from "redux-saga/effects";
import {
    getAdUnitAndFormatInfoRequest,
    getDevicesRequest,
    getInterestsRequest,
    getAgeRequest,
    getGenderRequest,
    getLanguagesRequest,
    getCurrenciesRequest,
    getTimezonesRequest,
    getRestrictionsRequest,
    getCountryListRequest,
    CampaignUrl,
    EditCampaignUrl,
    getCampaignsListRequest,
    getCampaignByIdRequest,
    getPurchasedCampaignDataFromCRM,
    getKPIRequest
} from "@common";
import { uploadCountryListAction } from "..";
import { LocationChangeAction } from "react-router-redux";
import { AppState } from "@store";
import { FetchingState } from "rd-redux-http";
import { PendingCampaign, CampaignInfo, KPIMapping } from "store/api";
import { changeRequiredDataLoadingStatusAction } from "../actions";
import {
    campaignMediaPlanForm,
    CampaignMediaPlanFormFields,
    campaignBudgetForm,
    CampaignBudgetFormFields,
    campaignCreativesForm,
    CampaignCreativesFormFields
} from "../state";
import { recalculateEstimatedImpression } from "./recalculateEstimatedImpression";

export function* loadDataForCampaignPageWithRequiredFields(a: LocationChangeAction) {
    yield put(changeRequiredDataLoadingStatusAction({ status: true }));

    const currentPath = a.payload.pathname;
    const matchEditCampaignUrl = EditCampaignUrl.match(currentPath, true);
    const matchParams = matchEditCampaignUrl.isMatched && matchEditCampaignUrl.params;

    const defaultValues: Partial<PendingCampaign> = {
        priceModel: "CPM",
        timetable: {
            Sun: { morning: true, afternoon: true, night: true },
            Mon: { morning: true, afternoon: true, night: true },
            Tue: { morning: true, afternoon: true, night: true },
            Wed: { morning: true, afternoon: true, night: true },
            Thu: { morning: true, afternoon: true, night: true },
            Fri: { morning: true, afternoon: true, night: true },
            Sat: { morning: true, afternoon: true, night: true }
        },
        currency: "EUR",
        adUnitType: "Static Banner"
    };

    try {
        let appState: AppState = yield select();

        yield call(checkAndUploadMappings, appState);

        yield all([loadCountryList(), loadCampaignsList(a)]);

        appState = yield select();
        let startData: Partial<PendingCampaign> = {};

        const kpiMapping: any = FetchingState.isSuccess(appState.mappings.kpi) && appState.mappings.kpi.data;

        const primaryKPI =
            kpiMapping && kpiMapping.filter((kpi: KPIMapping) => kpi.unit === defaultValues.priceModel)[0].name;

        defaultValues.primaryKPI = primaryKPI ? primaryKPI : undefined;

        if (matchParams["campaignId"] === "new") {
            startData = setStartDataForNewCampaign(startData, defaultValues, appState);
        } else {
            const selectedCampaign: CampaignInfo = yield call(findSelectedCampaign, appState, matchParams);
            const isCampaignFromCRM: boolean =
                selectedCampaign.status === "completed" ||
                selectedCampaign.status === "pending" ||
                selectedCampaign.status === "running";

            if (isCampaignFromCRM) {
                yield* getPurchasedCampaignDataFromCRM.saga({
                    clientId: matchParams.clientId,
                    campaignId: matchParams.campaignId,
                    _q: new Date().getTime()
                });
            } else {
                yield* getCampaignByIdRequest.saga({
                    clientId: matchParams.clientId,
                    campaignId: matchParams.campaignId
                });
            }

            appState = yield select();
            startData = setDataForExistingCampaign(startData, defaultValues, isCampaignFromCRM, appState);
        }

        yield put(campaignMediaPlanForm.actions.setData(startData as CampaignMediaPlanFormFields));
        yield put(campaignCreativesForm.actions.setData(startData as CampaignCreativesFormFields));
        yield put(campaignBudgetForm.actions.setData(startData as CampaignBudgetFormFields));

        yield put(changeRequiredDataLoadingStatusAction({ status: false }));

        yield* recalculateEstimatedImpression();
    } catch (e) {
        console.log(e);
        yield put(changeRequiredDataLoadingStatusAction({ status: false }));
    }
}

export function* checkAndUploadMappings(appState: AppState) {
    const mappingsState = appState.mappings;

    if (
        !FetchingState.isSuccess(mappingsState.adunits) ||
        !FetchingState.isSuccess(mappingsState.kpi) ||
        !FetchingState.isSuccess(mappingsState.devices) ||
        !FetchingState.isSuccess(mappingsState.interests) ||
        !FetchingState.isSuccess(mappingsState.age) ||
        !FetchingState.isSuccess(mappingsState.gender) ||
        !FetchingState.isSuccess(mappingsState.languages) ||
        (!FetchingState.isSuccess(mappingsState.currencies) && !FetchingState.isSuccess(mappingsState.timezones)) ||
        !FetchingState.isSuccess(mappingsState.restrictions)
    ) {
        yield all([
            getAdUnitAndFormatInfoRequest.saga({}),
            getKPIRequest.saga({}),
            getDevicesRequest.saga({}),
            getInterestsRequest.saga({}),
            getAgeRequest.saga({}),
            getGenderRequest.saga({}),
            getLanguagesRequest.saga({}),
            getCurrenciesRequest.saga({}),
            getTimezonesRequest.saga({}),
            getRestrictionsRequest.saga({})
        ]);
    }
}

export function* loadCountryList() {
    try {
        const response = yield getCountryListRequest({});
        if (response.ok) {
            yield put(uploadCountryListAction({ countryList: response.result }));
        }
    } catch (e) {
        console.log("Country list upload error", e.message);
    }
}

export function* loadCampaignsList(a: LocationChangeAction) {
    try {
        const {
            campaignList: { campaigns }
        } = (yield select()) as AppState;

        let urlMatch = CampaignUrl.match(a.payload, true);

        if (!urlMatch.isMatched) {
            urlMatch = EditCampaignUrl.match(a.payload, true);
        }

        if (urlMatch.isMatched) {
            if (FetchingState.hasParams(campaigns) && campaigns.params.clientId === urlMatch.params.clientId) {
                return;
            }

            yield* getCampaignsListRequest.saga({ clientId: urlMatch.params.clientId, _q: new Date().getTime() });
        }
    } catch (e) {
        console.error(e);
    }
}

function setStartDataForNewCampaign(
    startData: Partial<PendingCampaign>,
    defaultValues: Partial<PendingCampaign>,
    appState: AppState
): Partial<PendingCampaign> {
    startData.priceModel = defaultValues.priceModel;
    startData.primaryKPI = defaultValues.primaryKPI;
    // startData.secondaryKPI = defaultValues.secondaryKPI;
    startData.timetable = defaultValues.timetable;

    startData.adUnitFormat = appState.mappings.adunits.fetchState === "ok" && appState.mappings.adunits.data[0].size;
    startData.currency = defaultValues.currency;
    startData.adUnitType =
        appState.mappings.adunits.fetchState === "ok" &&
        appState.mappings.adunits.data[0].types.find(t => t.name === "Static Banner").name;

    return startData;
}

function* findSelectedCampaign(appState: AppState, matchParams: any) {
    let selectedCampaign: CampaignInfo =
        FetchingState.isSuccess(appState.campaignList.campaigns) &&
        appState.campaignList.campaigns.data.find(campaign => campaign.campaignId === matchParams.campaignId);

    if (!selectedCampaign) {
        const camapaignList = yield* getCampaignsListRequest.saga({
            clientId: matchParams.clientId,
            _q: new Date().getTime()
        });

        selectedCampaign = camapaignList.find(
            (campaign: CampaignInfo) => campaign.campaignId === matchParams.campaignId
        );
    }

    return selectedCampaign;
}

function setDataForExistingCampaign(
    startData: Partial<PendingCampaign>,
    defaultValues: Partial<PendingCampaign>,
    isCampaignFromCRM: boolean,
    appState: AppState
): Partial<PendingCampaign> {
    const existingCampaign: Partial<PendingCampaign> = isCampaignFromCRM
        ? FetchingState.isSuccess(appState.campaignState.summaryCampaign) && appState.campaignState.summaryCampaign.data
        : FetchingState.isSuccess(appState.campaignState.editingCampaign) &&
          appState.campaignState.editingCampaign.data.result;

    startData = { ...existingCampaign };

    startData.priceModel = existingCampaign.priceModel ? existingCampaign.priceModel : defaultValues.priceModel;

    startData.primaryKPI = existingCampaign.primaryKPI ? existingCampaign.primaryKPI : defaultValues.primaryKPI;

    // startData.secondaryKPI = existingCampaign.secondaryKPI ? existingCampaign.secondaryKPI : defaultValues.secondaryKPI;

    startData.timetable = existingCampaign.timetable ? existingCampaign.timetable : defaultValues.timetable;

    startData.adUnitFormat = existingCampaign.adUnitFormat
        ? existingCampaign.adUnitFormat
        : appState.mappings.adunits.fetchState === "ok" && appState.mappings.adunits.data[0].size;

    startData.currency = existingCampaign.currency ? existingCampaign.currency : defaultValues.currency;

    startData.adUnitType = existingCampaign.adUnitType
        ? existingCampaign.adUnitType
        : appState.mappings.adunits.fetchState === "ok" &&
          appState.mappings.adunits.data[0].types.find(t => t.name === "Static Banner").name;

    return startData;
}
